// src/pages/about/changelog/Changelog.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TablePagination,
    IconButton,
    Stack,
    Snackbar,
    TextField,
    Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { useTheme } from "@mui/material/styles";
import '../../../components/css/ded-style.css';
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext } from "react-router-dom";

// Import AddChangelog component
import AddChangelog from "./add-changelog/AddChangelog";
import ChangelogDetail from "./changelog-detail/ChangelogDetail";
import CustomBreadcrumbs from "../../../components/universal-features/breadcrumbs";
import LocalStorageInfo from "./LocalStorageInfo";

// Import fungsi dari service terpisah
import {
    fetchChangelogFromGoogleSheets,
    deleteFromGoogleSheets,
    formatTimestamp
} from "./googleSheetsService";

// Import handler untuk save changelog
import { handleSaveChangelog } from "./add-changelog/saveChangelogHandler";
import { Block } from "@mui/icons-material";

function Changelog() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedChangelog, setSelectedChangelog] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        datetime: '',
        status: ''
    });

    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [detailChangelog, setDetailChangelog] = useState(null);
    const [contextMenu, setContextMenu] = useState({
        anchorEl: null,
        selectedItem: null,
    });

    const paperShadow = "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)";

    // State untuk menyimpan data user dari localStorage
    const [userDataFromStorage, setUserDataFromStorage] = useState(null);

    // Effect untuk membaca data user dari localStorage saat komponen mount
    useEffect(() => {
        const getUserDataFromLocalStorage = () => {
            try {
                // Cari semua key di localStorage
                const allKeys = Object.keys(localStorage);
                
                // Cari key yang mengandung data user
                // Biasanya key untuk user data mengandung 'user' atau 'auth'
                for (const key of allKeys) {
                    try {
                        const dataString = localStorage.getItem(key);
                        if (dataString) {
                            const data = JSON.parse(dataString);
                            
                            // Periksa apakah data memiliki struktur seperti yang ditunjukkan
                            if (data && data.data && data.data.role) {
                                // Struktur: {data: {role: "developer", ...}, ...}
                                setUserDataFromStorage(data.data);
                                return;
                            } else if (data && data.role) {
                                // Struktur: {role: "developer", ...}
                                setUserDataFromStorage(data);
                                return;
                            } else if (data && data.user && data.user.role) {
                                // Struktur: {user: {role: "developer", ...}, ...}
                                setUserDataFromStorage(data.user);
                                return;
                            }
                        }
                    } catch (parseError) {
                        // Skip jika bukan JSON
                        continue;
                    }
                }
                
                // Jika tidak ditemukan, coba mencari dengan pattern khusus
                // Berdasarkan informasi yang diberikan, mungkin key-nya adalah 'pineTracker_userData_J2xVpI8TbwOQwdmKYVuh6ES8wjI3'
                const pineTrackerKeys = allKeys.filter(key => key.includes('pineTracker'));
                for (const key of pineTrackerKeys) {
                    try {
                        const dataString = localStorage.getItem(key);
                        if (dataString) {
                            const data = JSON.parse(dataString);
                            if (data && data.data && data.data.role) {
                                setUserDataFromStorage(data.data);
                                return;
                            } else if (data && data.role) {
                                setUserDataFromStorage(data);
                                return;
                            }
                        }
                    } catch (e) {
                        continue;
                    }
                }
                
                console.log("No user data found in localStorage");
                setUserDataFromStorage(null);
            } catch (error) {
                console.error("Error reading user data from localStorage:", error);
                setUserDataFromStorage(null);
            }
        };

        getUserDataFromLocalStorage();
        
        // Juga periksa localStorage setiap kali ada perubahan
        const handleStorageChange = () => {
            getUserDataFromLocalStorage();
        };
        
        // Listen for storage changes
        window.addEventListener('storage', handleStorageChange);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Function untuk mendapatkan user data dari localStorage dengan cara yang lebih reliable
    const getStoredUserData = () => {
        try {
            // Coba cari key yang mengandung 'pineTracker'
            const keys = Object.keys(localStorage);
            for (const key of keys) {
                if (key.includes('pineTracker') || key.includes('user')) {
                    const item = localStorage.getItem(key);
                    if (item) {
                        const data = JSON.parse(item);
                        // Cek berbagai kemungkinan struktur
                        if (data?.data?.role) {
                            return data.data;
                        } else if (data?.role) {
                            return data;
                        } else if (data?.user?.role) {
                            return data.user;
                        }
                    }
                }
            }
            
            // Jika tidak ditemukan dengan pattern, coba semua key
            for (const key of keys) {
                try {
                    const item = localStorage.getItem(key);
                    if (item && item.includes('developer')) {
                        const data = JSON.parse(item);
                        if (data?.role || data?.data?.role) {
                            return data.data || data;
                        }
                    }
                } catch (e) {
                    continue;
                }
            }
            
            return null;
        } catch (error) {
            console.error("Error getting stored user data:", error);
            return null;
        }
    };

    // Effect untuk fetch data changelog
    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                const loadedData = await fetchChangelogFromGoogleSheets();
                loadedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setOriginalDataList(loadedData);
                
                // Dapatkan user data langsung dari localStorage saat filtering
                const storedUserData = getStoredUserData();
                const userRole = storedUserData?.role;
                
                if (userRole && userRole.toLowerCase() === 'developer') {
                    setDataList(loadedData);
                } else {
                    // Sembunyikan draft untuk non-developer
                    const filteredData = loadedData.filter(item => item.status !== 'Draft');
                    setDataList(filteredData);
                }
            } catch (error) {
                console.error("Error fetching changelog data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data changelog: " + error.message,
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) fetchData();
    }, [user]);

    // Effect untuk filter data saat search query berubah
    useEffect(() => {
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            const filtered = originalDataList.filter(item => (
                (item.title && item.title.toLowerCase().includes(query)) ||
                (item.category && item.category.toLowerCase().includes(query)) ||
                (item.description && item.description.toLowerCase().includes(query)) ||
                (item.user && item.user.toLowerCase().includes(query)) ||
                (item.details && item.details.toLowerCase().includes(query)) ||
                (item.status && item.status.toLowerCase().includes(query))
            ));
            
            // Filter berdasarkan role
            const storedUserData = getStoredUserData();
            const userRole = storedUserData?.role;
            
            if (userRole && userRole.toLowerCase() === 'developer') {
                setDataList(filtered);
            } else {
                setDataList(filtered.filter(item => item.status !== 'Draft'));
            }
        } else {
            // Filter berdasarkan role
            const storedUserData = getStoredUserData();
            const userRole = storedUserData?.role;
            
            if (userRole && userRole.toLowerCase() === 'developer') {
                setDataList(originalDataList);
            } else {
                setDataList(originalDataList.filter(item => item.status !== 'Draft'));
            }
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    // Handler functions
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });
    const handleSearchChange = (event) => setSearchQuery(event.target.value);
    
    const handleAddChangelog = () => {
        setDialogMode('add');
        setFormData({
            title: '',
            description: '',
            category: '',
            datetime: new Date().toISOString().slice(0, 16),
            status: 'Draft'
        });
        setDialogOpen(true);
    };

    const handleEditChangelog = (changelog) => {
        setDialogMode('edit');
        setSelectedChangelog(changelog);
        setFormData({
            title: changelog.title || '',
            description: changelog.description || '',
            category: changelog.category || '',
            datetime: changelog.datetime ? new Date(changelog.datetime).toISOString().slice(0, 16) : new Date().toISOString().slice(0, 16),
            status: changelog.status || 'Draft'
        });
        setDialogOpen(true);
    };

    const handleViewChangelog = (changelog) => {
        setDetailChangelog(changelog);
        setDetailDialogOpen(true);
    };

    const handleEditFromDetail = (changelog) => {
        setDetailDialogOpen(false);
        handleEditChangelog(changelog);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedChangelog(null);
        setFormData({ title: '', description: '', category: '', datetime: '', status: '' });
    };

    const handleCloseDetailDialog = () => {
        setDetailDialogOpen(false);
        setDetailChangelog(null);
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChangelogWrapper = async () => {
        await handleSaveChangelog({
            formData,
            user,
            dialogMode,
            selectedChangelog,
            setSnackbar,
            setDialogOpen,
            setSelectedChangelog,
            setFormData,
            setOriginalDataList,
            setDataList,
            setPage,
            setIsSaving
        });
    };

    const handleDeleteChangelog = async (changelog) => {
        if (!window.confirm(`Apakah Anda yakin ingin menghapus changelog "${changelog.title}"?`)) return;

        try {
            setIsSaving(true);
            const result = await deleteFromGoogleSheets(changelog.id);
            
            if (result.success) {
                setSnackbar({ open: true, message: `Changelog berhasil dihapus!`, severity: "success" });
                const loadedData = await fetchChangelogFromGoogleSheets();
                if (loadedData.length > 0) {
                    loadedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    setOriginalDataList(loadedData);
                    
                    // Filter berdasarkan role
                    const storedUserData = getStoredUserData();
                    const userRole = storedUserData?.role;
                    
                    if (userRole && userRole.toLowerCase() === 'developer') {
                        setDataList(loadedData);
                    } else {
                        setDataList(loadedData.filter(item => item.status !== 'Draft'));
                    }
                    setPage(0);
                } else {
                    setOriginalDataList([]);
                    setDataList([]);
                }
            } else {
                throw new Error(result.message || 'Gagal menghapus dari Google Sheets');
            }
        } catch (error) {
            console.error("Error deleting changelog:", error);
            setSnackbar({ open: true, message: `Gagal menghapus changelog: ${error.message}`, severity: "error" });
        } finally {
            setIsSaving(false);
            setContextMenu({ anchorEl: null, selectedItem: null });
        }
    };

    const handleContextMenuOpen = (event, item) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({ anchorEl: event.currentTarget, selectedItem: item });
    };

    const handleContextMenuClose = () => {
        setContextMenu({ anchorEl: null, selectedItem: null });
    };

    const handleRefreshData = async () => {
        setLoadingData(true);
        try {
            const loadedData = await fetchChangelogFromGoogleSheets();
            if (loadedData.length > 0) {
                loadedData.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                setOriginalDataList(loadedData);
                
                // Filter berdasarkan role
                const storedUserData = getStoredUserData();
                const userRole = storedUserData?.role;
                
                if (userRole && userRole.toLowerCase() === 'developer') {
                    setDataList(loadedData);
                } else {
                    setDataList(loadedData.filter(item => item.status !== 'Draft'));
                }
                setPage(0);
                setSnackbar({ open: true, message: `Data diperbarui. Total: ${loadedData.length} changelog`, severity: "info" });
            }
        } catch (error) {
            console.error("Error refreshing data:", error);
            setSnackbar({ open: true, message: "Gagal memperbarui data: " + error.message, severity: "error" });
        } finally {
            setLoadingData(false);
        }
    };

    // Fungsi helper untuk memeriksa apakah user adalah developer
    const checkIfDeveloper = () => {
        const storedUserData = getStoredUserData();
        return storedUserData?.role?.toLowerCase() === 'developer';
    };

    const developerMode = checkIfDeveloper();

    if (authLoading || loadingData) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses halaman ini</Alert>
            </Box>
        );
    }

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            <CustomBreadcrumbs breadcrumbItems={breadcrumbs} />

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, boxShadow: paperShadow, mb: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">Changelog</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari perubahan..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
                            sx={{ width: 250 }}
                        />
                        <Tooltip title="Refresh Data">
                            <IconButton 
                                color="primary" 
                                size="small" 
                                onClick={handleRefreshData}
                                sx={{ backgroundColor: theme.palette.primary.light, color: theme.palette.primary.contrastText,
                                    '&:hover': { backgroundColor: theme.palette.primary.main } }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                        {/* Hanya tampilkan tombol Tambah Changelog Baru jika user adalah developer */}
                        {developerMode && (
                            <Tooltip title="Tambah Changelog Baru">
                                <IconButton 
                                    color="primary" 
                                    size="small" 
                                    onClick={handleAddChangelog}
                                    sx={{ backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText,
                                        '&:hover': { backgroundColor: theme.palette.primary.dark } }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                {displayedDataList.length > 0 ? (
                    <>
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'auto', maxHeight: 500 }}>
                            <Table stickyHeader aria-label="changelog table">
                                <TableHead>
                                    <TableRow className="tr">
                                        <TableCell className="ded-tch">Title</TableCell>
                                        <TableCell className="ded-tch">Category</TableCell>
                                        <TableCell className="ded-tch">Created At</TableCell>
                                        <TableCell className="ded-tch">Status</TableCell>
                                        <TableCell className="ded-tch">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
<TableBody>
    {displayedDataList
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((row, index) => (
            <TableRow key={row.id || index} hover onContextMenu={(e) => handleContextMenuOpen(e, row)}>
                <TableCell className="ded-tbtrtc">
                    <Typography variant="body2" fontWeight="medium">{row.title || row.details || '-'}</Typography>
                </TableCell>
                <TableCell className="ded-tbtrtc">
                    <Typography variant="body2" fontWeight="medium">{row.category || row.module || '-'}</Typography>
                </TableCell>
                <TableCell className="ded-tbtrtc">
                    <Typography variant="body2">{formatTimestamp(row.createdAt)}</Typography>
                </TableCell>
                <TableCell className="ded-tbtrtc">
                    <Box sx={{ display: 'inline-block', px: 1, py: 0.5, borderRadius: 1,
                        backgroundColor: row.status === 'Published' ? '#e8f5e9' : row.status === 'Draft' ? '#fff3e0' : '#f5f5f5',
                        color: row.status === 'Published' ? '#2e7d32' : row.status === 'Draft' ? '#f57c00' : '#616161',
                        fontWeight: 'medium', fontSize: '0.75rem' }}
                    >
                        {row.status || '-'}
                    </Box>
                </TableCell>
                <TableCell className="ded-tbtrtc" sx={{ textAlign: 'center' }}>
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Lihat Detail">
                            <IconButton size="small" onClick={() => handleViewChangelog(row)} color="info">
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {/* Hanya tampilkan tombol Edit jika user adalah developer */}
                        {developerMode && (
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleEditChangelog(row)} color="primary">
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {/* Hanya tampilkan tombol Hapus jika user adalah developer */}
                        {developerMode && (
                            <Tooltip title="Hapus">
                                <IconButton size="small" onClick={() => handleDeleteChangelog(row)} color="error">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </TableCell>
            </TableRow>
        ))}
</TableBody>
                            </Table>
                        </Box>
                        <TablePagination
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            component="div"
                            count={displayedDataList.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { margin: 0 } }}
                        />
                    </>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200,
                        border: '1px dashed #e0e0e0', borderRadius: 1 }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            {searchQuery ? 'Tidak ada data changelog yang sesuai dengan pencarian' : 'Tidak ada data changelog.'}
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* LocalStorage Info Section - SUPER CLEAN! */}
            {developerMode && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, boxShadow: paperShadow }}>
                    <LocalStorageInfo />
                </Paper>
            )}
            
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <AddChangelog open={dialogOpen} onClose={handleCloseDialog} formData={formData}
                onFormChange={handleFormChange} onSave={handleSaveChangelogWrapper} snackbar={snackbar}
                onCloseSnackbar={handleCloseSnackbar} mode={dialogMode}
            />

            <ChangelogDetail open={detailDialogOpen} onClose={handleCloseDetailDialog}
                changelog={detailChangelog} onEdit={handleEditFromDetail}
                showEditButton={true} showBackButton={false}
            />

            {isSaving && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
                    justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
                >
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
                        {dialogMode === 'add' ? 'Menyimpan ke Google Sheets...' : 
                         contextMenu.selectedItem ? 'Menghapus dari Google Sheets...' : 'Memproses...'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default Changelog;